import { type FC, Fragment, useCallback, useMemo } from "react";

import { Checkbox, Group, Table, Text, Tooltip } from "@mantine/core";
import classNames from "clsx";
import { useIntl } from "react-intl";

import {
    disabledRow,
    headerCell,
    row,
    stickyHeader,
    table as tableClass,
} from "../../shared/components/styled-table/StyledTable.module.css";
import { useInfiniteScroll } from "../hooks/useInfiniteScroll";
import { formatBytes } from "../utils/formatBytes";

import { RemoteAssetLensUsageExpandContent } from "./RemoteAssetLensUsageExpandContent";
import { RemoteAssetPreview } from "./RemoteAssetPreview";
import {
    changedText,
    checkboxCell,
    copyingAllowed,
    copyingCell,
    copyingDenied,
    emptyCell,
    expandLensList,
    expandRow,
    lensLink,
    moreLink,
    nameCell,
    nameContent,
    nameText,
    rowSelected,
    sizeCell,
    statusText,
    typeCell,
    updatedCell,
    usedByActive,
    usedByCell,
    usedByToggle,
} from "./RemoteAssetsTable.module.css";
import { type RemoteAssetListItem } from "./RemoteAssetsTable.types";
import { type RemoteAssetsMode } from "./RemoteAssetsToolbar";
import {
    ColumnCopying,
    ColumnName,
    ColumnSize,
    ColumnType,
    ColumnUpdated,
    ColumnUsedBy,
    LensUsages,
    LensUsagesToggle,
    LensUsagesToggleOpen,
    LoadingMoreAssets,
    StatusChanged,
    StatusInUse,
} from "./messages.generated";

interface RemoteAssetsTableProps {
    assets: readonly RemoteAssetListItem[];
    mode: RemoteAssetsMode;
    checkedIds: Set<string>;
    onToggleChecked: (id: string) => void;
    onToggleAll: () => void;
    onRowClick: (assetId: string) => void;
    originalForkingAllowed: Map<string, boolean>;
    orgId: string;
    expandedIds: Set<string>;
    onToggleExpand: (id: string) => void;
    hasMore: boolean;
    isFetchingMore: boolean;
    onLoadMore: () => void;
}

export const RemoteAssetsTable: FC<RemoteAssetsTableProps> = ({
    assets,
    mode,
    checkedIds,
    onToggleChecked,
    onToggleAll,
    onRowClick,
    originalForkingAllowed,
    orgId,
    expandedIds,
    onToggleExpand,
    hasMore,
    isFetchingMore,
    onLoadMore,
}) => {
    const intl = useIntl();
    const sentinelRef = useInfiniteScroll<HTMLTableRowElement>({ hasMore, isFetchingMore, onLoadMore });

    const isDeleteMode = mode === "delete";
    const isEditForkingMode = mode === "editCopying";
    const showDeleteCheckboxes = isDeleteMode;

    const selectAllState = useMemo(() => {
        const eligibleAssets = isDeleteMode ? assets.filter((asset) => asset.lensUsages === 0) : assets;
        if (eligibleAssets.length === 0) return "unchecked" as const;
        const allChecked = eligibleAssets.every((asset) => checkedIds.has(asset.id));
        const someChecked = eligibleAssets.some((asset) => checkedIds.has(asset.id));
        if (allChecked) return "checked" as const;
        if (someChecked) return "indeterminate" as const;
        return "unchecked" as const;
    }, [assets, checkedIds, isDeleteMode]);

    const handleRowClick = useCallback(
        (asset: RemoteAssetListItem) => {
            if (isDeleteMode) {
                if (asset.lensUsages > 0) return;
                onToggleChecked(asset.id);
            } else if (isEditForkingMode) {
                onToggleChecked(asset.id);
            } else {
                onRowClick(asset.id);
            }
        },
        [isDeleteMode, isEditForkingMode, onToggleChecked, onRowClick],
    );

    const rows = useMemo(
        () =>
            assets.map((asset) => {
                const isInUse = isDeleteMode && asset.lensUsages > 0;
                const hasChanged =
                    isEditForkingMode && !!originalForkingAllowed.get(asset.id) !== checkedIds.has(asset.id);

                return (
                    <Fragment key={asset.id}>
                        <Table.Tr
                            className={classNames(
                                row,
                                isInUse && disabledRow,
                                isDeleteMode && checkedIds.has(asset.id) && rowSelected,
                                isEditForkingMode && hasChanged && rowSelected,
                            )}
                            onClick={() => {
                                handleRowClick(asset);
                            }}
                        >
                            <Table.Td className={checkboxCell}>
                                <Checkbox
                                    checked={checkedIds.has(asset.id)}
                                    disabled={isInUse}
                                    onChange={() => {
                                        onToggleChecked(asset.id);
                                    }}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                    }}
                                    style={showDeleteCheckboxes ? undefined : { visibility: "hidden" }}
                                />
                            </Table.Td>
                            <Table.Td className={nameCell}>
                                <Group gap="sm" wrap="nowrap" className={nameContent}>
                                    <RemoteAssetPreview type={asset.type} previewImageUrl={asset.previewImageUrl} />
                                    <Text size="sm" fw={500} className={nameText}>
                                        {asset.name}
                                    </Text>
                                    {isInUse && (
                                        <Text size="xs" c="dimmed" className={statusText}>
                                            <StatusInUse />
                                        </Text>
                                    )}
                                    {hasChanged && (
                                        <Text size="xs" className={`${statusText} ${changedText}`}>
                                            <StatusChanged />
                                        </Text>
                                    )}
                                </Group>
                            </Table.Td>
                            <Table.Td className={typeCell}>
                                <Text size="sm">{asset.type}</Text>
                            </Table.Td>
                            <Table.Td className={copyingCell}>
                                {isEditForkingMode ? (
                                    <Checkbox
                                        checked={checkedIds.has(asset.id)}
                                        onChange={() => {
                                            onToggleChecked(asset.id);
                                        }}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                        }}
                                    />
                                ) : (
                                    <Text size="sm" className={asset.forkingAllowed ? copyingAllowed : copyingDenied}>
                                        {asset.forkingAllowed ? "✓" : "—"}
                                    </Text>
                                )}
                            </Table.Td>
                            <Table.Td className={sizeCell}>
                                <Text size="sm">{formatBytes(asset.assetSize)}</Text>
                            </Table.Td>
                            <Table.Td className={usedByCell}>
                                {asset.lensUsages > 0 && mode === "normal" ? (
                                    <button
                                        type="button"
                                        className={usedByToggle}
                                        aria-expanded={expandedIds.has(asset.id)}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            onToggleExpand(asset.id);
                                        }}
                                    >
                                        {expandedIds.has(asset.id) ? (
                                            <LensUsagesToggleOpen count={asset.lensUsages} />
                                        ) : (
                                            <LensUsagesToggle count={asset.lensUsages} />
                                        )}
                                    </button>
                                ) : (
                                    <Text size="sm" className={asset.lensUsages > 0 ? usedByActive : undefined}>
                                        <LensUsages count={asset.lensUsages} />
                                    </Text>
                                )}
                            </Table.Td>
                            <Table.Td className={updatedCell}>
                                <Tooltip
                                    label={intl.formatDate(new Date(asset.updatedAt), {
                                        month: "short",
                                        day: "numeric",
                                        year: "numeric",
                                        hour: "numeric",
                                        minute: "2-digit",
                                    })}
                                >
                                    <Text size="sm">
                                        {intl.formatDate(new Date(asset.updatedAt), { month: "short", day: "numeric" })}
                                    </Text>
                                </Tooltip>
                            </Table.Td>
                        </Table.Tr>
                        {expandedIds.has(asset.id) && mode === "normal" && (
                            <Table.Tr className={expandRow}>
                                <Table.Td colSpan={7}>
                                    <RemoteAssetLensUsageExpandContent
                                        assetId={asset.id}
                                        lensUsages={asset.lensUsages}
                                        orgId={orgId}
                                        containerClassName={expandLensList}
                                        linkClassName={lensLink}
                                        moreClassName={moreLink}
                                        textSize="sm"
                                    />
                                </Table.Td>
                            </Table.Tr>
                        )}
                    </Fragment>
                );
            }),
        [
            assets,
            checkedIds,
            expandedIds,
            handleRowClick,
            intl,
            isDeleteMode,
            isEditForkingMode,
            mode,
            onToggleChecked,
            onToggleExpand,
            orgId,
            originalForkingAllowed,
            showDeleteCheckboxes,
        ],
    );

    return (
        <Table highlightOnHover={false} horizontalSpacing="md" verticalSpacing="sm" className={tableClass}>
            <Table.Thead className={stickyHeader}>
                <Table.Tr>
                    <Table.Th className={`${headerCell} ${checkboxCell}`}>
                        <Checkbox
                            checked={selectAllState === "checked"}
                            indeterminate={selectAllState === "indeterminate"}
                            onChange={onToggleAll}
                            style={showDeleteCheckboxes ? undefined : { visibility: "hidden" }}
                        />
                    </Table.Th>
                    <Table.Th className={`${headerCell} ${nameCell}`}>
                        <ColumnName />
                    </Table.Th>
                    <Table.Th className={`${headerCell} ${typeCell}`}>
                        <ColumnType />
                    </Table.Th>
                    <Table.Th className={`${headerCell} ${copyingCell}`}>
                        {isEditForkingMode ? (
                            <Checkbox
                                checked={selectAllState === "checked"}
                                indeterminate={selectAllState === "indeterminate"}
                                onChange={onToggleAll}
                            />
                        ) : (
                            <ColumnCopying />
                        )}
                    </Table.Th>
                    <Table.Th className={`${headerCell} ${sizeCell}`}>
                        <ColumnSize />
                    </Table.Th>
                    <Table.Th className={`${headerCell} ${usedByCell}`}>
                        <ColumnUsedBy />
                    </Table.Th>
                    <Table.Th className={`${headerCell} ${updatedCell}`}>
                        <ColumnUpdated />
                    </Table.Th>
                </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
                {rows}
                {hasMore && (
                    <Table.Tr ref={sentinelRef}>
                        <Table.Td colSpan={7} className={emptyCell}>
                            <Text c="dimmed" size="sm">
                                {isFetchingMore ? <LoadingMoreAssets /> : null}
                            </Text>
                        </Table.Td>
                    </Table.Tr>
                )}
            </Table.Tbody>
        </Table>
    );
};
